import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Coffee, Clock, AlertTriangle, CheckCircle, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function CaffeineGuide() {
  const navigate = useNavigate();

  const caffeineSources = [
    { item: "Espresso (1 shot)", amount: "63mg", notes: "Quick hit, fast absorption" },
    { item: "Coffee (8oz)", amount: "95mg", notes: "Standard drip coffee" },
    { item: "Cold Brew (8oz)", amount: "200mg", notes: "Often 2x regular coffee!" },
    { item: "Black Tea (8oz)", amount: "47mg", notes: "Also contains L-theanine for calm" },
    { item: "Green Tea (8oz)", amount: "28mg", notes: "Gentler energy boost" },
    { item: "Soda (12oz)", amount: "30-45mg", notes: "Plus lots of sugar" },
    { item: "Dark Chocolate (1oz)", amount: "12mg", notes: "Small but adds up" },
    { item: "Energy Drink (8oz)", amount: "75-150mg", notes: "Varies widely by brand" }
  ];

  const cutoffRules = [
    { rule: "2 PM Cutoff", why: "Caffeine has a 6-hour half-life—coffee at 3 PM means half is still in your system at 9 PM" },
    { rule: "Limit to 400mg Daily", why: "FDA recommended maximum for adults—about 4 cups of coffee" },
    { rule: "Morning is Best", why: "Aligns with natural cortisol peaks for maximum benefit" },
    { rule: "Watch Hidden Sources", why: "Chocolate, tea, some medications, and decaf (yes, it has some!)" }
  ];

  const alternatives = [
    { drink: "Herbal Tea", benefit: "Warm, ritualistic, naturally caffeine-free", examples: "Chamomile, peppermint, rooibos" },
    { drink: "Decaf Coffee", benefit: "Same ritual, 97% less caffeine", examples: "Still has 2-15mg per cup" },
    { drink: "Golden Milk", benefit: "Turmeric + warming spices, anti-inflammatory", examples: "Turmeric, ginger, cinnamon, milk" },
    { drink: "Warm Water + Lemon", benefit: "Hydrating, digestive support", examples: "Add honey if desired" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4">
            Week 5 • Day 6 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Caffeine Awareness Day
          </h1>
          <p className="text-xl text-secondary-600">Understanding Your Caffeine Habits</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Coffee className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Experiment</h3>
              <p className="text-amber-100 text-lg">
                Track every caffeinated drink today with the time consumed. Have your last caffeine by 2 PM. 
                Tonight, notice how this affects your evening energy and sleep onset.
              </p>
            </div>
          </div>
        </section>

        {/* Caffeine Content */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Know Your Caffeine</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {caffeineSources.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <div>
                  <span className="font-medium text-secondary-900">{item.item}</span>
                  <p className="text-xs text-secondary-500">{item.notes}</p>
                </div>
                <span className="font-bold text-amber-600">{item.amount}</span>
              </div>
            ))}
          </div>
        </section>

        {/* The Rules */}
        <section className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-amber-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Clock className="w-6 h-6 text-amber-500" />
            Caffeine Rules for Better Sleep
          </h2>
          <div className="space-y-4">
            {cutoffRules.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <h3 className="font-bold text-amber-700 mb-1">{item.rule}</h3>
                <p className="text-sm text-secondary-600">{item.why}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Afternoon Alternatives */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Moon className="w-6 h-6 text-amber-500" />
            Afternoon Alternatives
          </h2>
          <div className="space-y-3">
            {alternatives.map((item, index) => (
              <div key={index} className="p-4 bg-amber-50 rounded-xl">
                <h3 className="font-bold text-amber-700">{item.drink}</h3>
                <p className="text-sm text-secondary-600 mb-1">{item.benefit}</p>
                <p className="text-xs text-secondary-500">Try: {item.examples}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default CaffeineGuide;
